import React, { useRef, useState } from 'react';

export interface BoxModelValues {
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  width?: string;
  height?: string;
}

export interface BoxModelWidgetProps {
  values: BoxModelValues;
  onChange: (patch: Record<string, string | undefined>) => void;
  onHoverBoxPart?: (part: 'margin' | 'padding' | null) => void;
}

function parseVal(raw?: string): number {
  if (!raw) return 0;
  if (raw === 'auto') return 0;
  const match = raw.match(/^(-?\d*\.?\d+)/);
  return match ? parseFloat(match[1]) : 0;
}

interface EditableQuadrantProps {
  propKey: string;
  value?: string;
  placeholder?: string;
  onChange: (key: string, val: string | undefined) => void;
  className?: string;
}

function EditableQuadrant({
  propKey,
  value,
  placeholder = '0',
  onChange,
  className = '',
}: EditableQuadrantProps): React.JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState('');
  const dragRef = useRef<{ startX: number; startY: number; startVal: number } | null>(null);

  const displayVal = value ? value.replace('px', '') : '-';

  const handlePointerDown = (e: React.PointerEvent) => {
    if (isEditing) return;
    const initialNum = parseVal(value);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startVal: initialNum };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dy = dragRef.current.startY - e.clientY;
    const dx = e.clientX - dragRef.current.startX;
    const delta = Math.abs(dy) > Math.abs(dx) ? dy : dx;
    const next = Math.max(0, Math.round(dragRef.current.startVal + delta));
    onChange(propKey, `${next}px`);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (dragRef.current) {
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      dragRef.current = null;
    }
  };

  if (isEditing) {
    return (
      <input
        type="text"
        autoFocus
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          setIsEditing(false);
          const clean = text.trim();
          if (clean === '' || clean === '-') {
            onChange(propKey, undefined);
          } else if (clean === 'auto') {
            onChange(propKey, 'auto');
          } else {
            const num = parseFloat(clean);
            onChange(propKey, !isNaN(num) ? `${num}px` : clean);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setIsEditing(false);
          }
        }}
        className="w-8 h-4 text-[10px] text-center font-mono border border-primary bg-background rounded px-0 py-0 focus:outline-none"
      />
    );
  }

  return (
    <span
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onDoubleClick={() => {
        setText(value ? value.replace('px', '') : '');
        setIsEditing(true);
      }}
      className={`text-[10px] font-mono cursor-ns-resize hover:text-foreground hover:font-bold select-none px-1 rounded transition-colors ${className}`}
      title="Drag to scrub spacing or double click to type (px)"
    >
      {displayVal}
    </span>
  );
}

export function BoxModelWidget({
  values,
  onChange,
  onHoverBoxPart,
}: BoxModelWidgetProps): React.JSX.Element {
  const handlePropChange = (key: string, val: string | undefined) => {
    onChange({ [key]: val });
  };

  return (
    <div className="flex flex-col gap-2 select-none">
      {/* Visual Box-Model Diagram */}
      <div
        className="relative bg-amber-500/10 border border-amber-500/40 rounded-lg p-3 flex flex-col items-center justify-between text-amber-800 dark:text-amber-200 transition-colors"
        onMouseEnter={() => onHoverBoxPart && onHoverBoxPart('margin')}
        onMouseLeave={() => onHoverBoxPart && onHoverBoxPart(null)}
      >
        <span className="absolute top-1 left-2 text-[9px] font-bold uppercase tracking-wider text-amber-600/80 dark:text-amber-400/80">
          margin
        </span>

        {/* Margin Top */}
        <EditableQuadrant
          propKey="marginTop"
          value={values.marginTop}
          onChange={handlePropChange}
          className="mb-1"
        />

        {/* Middle Layer (Border & Margin Left/Right) */}
        <div className="flex items-center justify-between w-full gap-2">
          {/* Margin Left */}
          <EditableQuadrant
            propKey="marginLeft"
            value={values.marginLeft}
            onChange={handlePropChange}
          />

          {/* Border Ring Box */}
          <div className="flex-1 bg-slate-500/10 border border-slate-400/40 rounded p-2.5 flex flex-col items-center justify-between text-muted-foreground">
            {/* Padding Box */}
            <div
              className="relative w-full bg-sky-500/10 border border-sky-500/40 rounded p-2.5 flex flex-col items-center justify-between text-sky-700 dark:text-sky-300 transition-colors"
              onMouseEnter={(e) => {
                e.stopPropagation();
                if (onHoverBoxPart) onHoverBoxPart('padding');
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                if (onHoverBoxPart) onHoverBoxPart('margin');
              }}
            >
              <span className="absolute top-0.5 left-1 text-[8px] font-bold uppercase tracking-wider text-sky-600/80 dark:text-sky-400/80">
                padding
              </span>

              {/* Padding Top */}
              <EditableQuadrant
                propKey="paddingTop"
                value={values.paddingTop}
                onChange={handlePropChange}
                className="mb-0.5"
              />

              <div className="flex items-center justify-between w-full gap-2">
                {/* Padding Left */}
                <EditableQuadrant
                  propKey="paddingLeft"
                  value={values.paddingLeft}
                  onChange={handlePropChange}
                />

                {/* Core Center (Dimensions) */}
                <div className="flex-1 py-1 px-2 bg-background/80 rounded border border-input text-center">
                  <span className="text-[10px] font-mono text-foreground font-semibold">
                    {values.width || 'auto'} × {values.height || 'auto'}
                  </span>
                </div>

                {/* Padding Right */}
                <EditableQuadrant
                  propKey="paddingRight"
                  value={values.paddingRight}
                  onChange={handlePropChange}
                />
              </div>

              {/* Padding Bottom */}
              <EditableQuadrant
                propKey="paddingBottom"
                value={values.paddingBottom}
                onChange={handlePropChange}
                className="mt-0.5"
              />
            </div>
          </div>

          {/* Margin Right */}
          <EditableQuadrant
            propKey="marginRight"
            value={values.marginRight}
            onChange={handlePropChange}
          />
        </div>

        {/* Margin Bottom */}
        <EditableQuadrant
          propKey="marginBottom"
          value={values.marginBottom}
          onChange={handlePropChange}
          className="mt-1"
        />
      </div>

      {/* Quick Spacing Presets */}
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-0.5">
        <button
          type="button"
          onClick={() =>
            onChange({
              paddingTop: '0px',
              paddingBottom: '0px',
              paddingLeft: '0px',
              paddingRight: '0px',
            })
          }
          className="px-2 py-0.5 text-[10px] rounded border border-border hover:bg-muted text-muted-foreground whitespace-nowrap cursor-pointer"
        >
          No Pad
        </button>
        <button
          type="button"
          onClick={() =>
            onChange({
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '16px',
              paddingRight: '16px',
            })
          }
          className="px-2 py-0.5 text-[10px] rounded border border-border hover:bg-muted text-muted-foreground whitespace-nowrap cursor-pointer"
        >
          16px Pad
        </button>
        <button
          type="button"
          onClick={() =>
            onChange({
              paddingTop: '32px',
              paddingBottom: '32px',
              paddingLeft: '24px',
              paddingRight: '24px',
            })
          }
          className="px-2 py-0.5 text-[10px] rounded border border-border hover:bg-muted text-muted-foreground whitespace-nowrap cursor-pointer"
        >
          32px Pad
        </button>
        <button
          type="button"
          onClick={() =>
            onChange({
              marginLeft: 'auto',
              marginRight: 'auto',
            })
          }
          className="px-2 py-0.5 text-[10px] rounded border border-border hover:bg-muted text-muted-foreground whitespace-nowrap cursor-pointer"
        >
          Center Auto
        </button>
      </div>
    </div>
  );
}
